import { useState, type JSX } from 'react'
import type { GospelMonth } from '../api'
import { monthLabel, yen } from '../format'
import { INFLOW_RAMP, POLARITY, CHART_INK, SERIES, hatchSwatch } from '../charts/palette'
import { compactYen, domainFor, monthLabelY, plotValue, ticksFor, yFor } from '../charts/scale'
import { hasPipeline, netExPipeline } from '../charts/plan'
import { NoDataZone } from '../charts/NoDataZone'
import { monthsAfter } from '../timeWindow'
import { PipelineToggle } from './PlanToggle'
import { SourceNote } from './FreshnessStatus'

// Money in above the line, money out below it, net cash as a line across the
// top of both.
//
// One axis, in yen, for all three — never a second scale on the right. The
// whole point of putting these together is that the reader can compare their
// heights directly, and a second scale silently destroys that.
//
// Inflow is stacked from steps of ONE hue rather than different colours: booked
// AR, fundraise and forecast AR are parts of the same quantity, so the
// relationship is sequential, not categorical.
//
// BOOKED VS PIPELINE. The view's net_cash_difference is
//   actual AR + fundraise + payroll + plan payroll + forecast AR − forecast AP.
// Forecast AR is weighted pipeline (deals not yet signed); it is the one
// optimistic term, and it lands in months whose AR is fully booked too. The
// default view leaves it out: booked money in, payroll (actual, or the plan's
// where none is reported) and planned costs out, so the net line is the
// current-status read. The header switch adds the pipeline back as its own
// hatched segment. Plan-derived COSTS are always drawn (hatched): future
// payroll exists only in the plan, and hiding it would show months with no
// burn. Every plan or pipeline segment is hatched, so the stack still adds up
// to the net line in both states.

const W = 720
// Bottom gutter carries angled month labels — H and PAD.bottom grew together
// from 260/34 so the plot box (PLOT_H) is unchanged and only the label gutter
// got taller.
const H = 274
const PAD = { top: 18, right: 16, bottom: 48, left: 56 }
const PLOT_W = W - PAD.left - PAD.right
const PLOT_H = H - PAD.top - PAD.bottom
const LABEL_Y = monthLabelY(PAD.top + PLOT_H)

type Hover = { index: number; x: number } | null

/** Drop a leading sign so an outflow can be printed after an explicit minus.
 *  String surgery only — money is never parsed to a float for display. */
const magnitude = (value: string | null): string | null =>
  value === null ? null : value.replace(/^[-+]/, '')

export function CashFlowChart({
  months,
  showPipeline,
  onShowPipelineChange,
  sourceNote,
  windowEnd,
  payrollLabel = 'Payroll out',
}: {
  months: GospelMonth[]
  showPipeline: boolean
  onShowPipelineChange: (next: boolean) => void
  /** Last month (YYYY-MM-01) of the page's time window; see NoDataZone. */
  windowEnd: string
  /** Set when a source behind this chart is out of date; see freshness.ts. */
  sourceNote?: string | null
  /** Legend for the outflow bars; names the payroll source in use. */
  payrollLabel?: string
}): JSX.Element {
  const [hover, setHover] = useState<Hover>(null)

  if (months.length === 0) {
    return (
      <section className="panel">
        <h2>Cash in and out</h2>
        <SourceNote text={sourceNote ?? null} />
        <p className="notice">No months in range.</p>
      </section>
    )
  }

  const rows = months.map((month) => {
    const ar = plotValue(month.actualAr) ?? 0
    const fundraise = plotValue(month.actualFundraise) ?? 0
    // Payroll is drawn below the line. Actual months use actualAp; a month with
    // no actuals yet falls back to the plan baseline (planPayroll) so the burn
    // still shows — drawn hatched to mark it planned, not reported. A genuinely
    // blank month (apBasis 'missing') has neither and draws no bar.
    const apRaw = plotValue(month.actualAp) ?? plotValue(month.planPayroll)
    const ap = apRaw === null ? null : -Math.abs(apRaw)
    const planned = month.apBasis === 'plan'
    // Outflows are drawn as MAGNITUDES below the line. The view sends payroll
    // negative and subtracts a positive forecast_net_new_ap, but either sign
    // convention reaches the same picture this way — and the stack then
    // reconciles with net_cash_difference under both, which a signed read would
    // not.
    const forecastIn = Math.abs(plotValue(month.forecastNetNewAr) ?? 0)
    const forecastOut = Math.abs(plotValue(month.forecastNetNewAp) ?? 0)
    // The net without the pipeline, subtracted EXACTLY from the decimal strings
    // (BigInt minor units), never through a float.
    const bookedNet = netExPipeline(month)
    // A month whose payroll is neither reported nor planned ('missing') has a
    // DB net with payroll at zero. Drawing it would read as a better month than
    // we can claim, which is the one thing this page must never do, so the net
    // line breaks there in both states and the tooltip says why.
    const payrollKnown = month.apBasis !== 'missing'
    return {
      month,
      ar,
      fundraise,
      ap,
      planned,
      forecastIn,
      forecastOut,
      net: plotValue(month.netCashDifference),
      bookedNet,
      payrollKnown,
    }
  })

  type Row = (typeof rows)[number]
  /** The net actually drawn under the current basis. */
  const netAt = (row: Row): number | null =>
    !row.payrollKnown ? null : showPipeline ? row.net : plotValue(row.bookedNet)

  const pipelineMonths = months.filter(hasPipeline).length

  // The domain is built from the PIPELINE-INCLUSIVE values in both states on
  // purpose: toggling the pipeline must not rescale the axis, or the booked bars
  // would appear to grow the moment it is switched off. Holding the scale still
  // is what turns the toggle into a measurement: the height that appears IS the
  // pipeline's contribution.
  const { min, max } = domainFor([
    ...rows.map((row) => row.ar + row.fundraise + row.forecastIn),
    ...rows.map((row) => (row.ap ?? 0) - row.forecastOut),
    ...rows.map((row) => row.net ?? 0),
    ...rows.map((row) => plotValue(row.bookedNet) ?? 0),
  ])

  // Months the window covers past the last one with data keep their place on the
  // axis. Marks only ever iterate `rows`, so nothing is drawn, and nothing is
  // zero, out there.
  const lastMonth = months[months.length - 1]?.month ?? windowEnd
  const gap = monthsAfter(lastMonth, windowEnd)
  const band = PLOT_W / (rows.length + gap.length)
  const barW = Math.min(26, band * 0.5)
  const y = (value: number): number => yFor(value, min, max, PAD.top, PLOT_H)
  const zero = y(0)
  const centre = (index: number): number => PAD.left + band * index + band / 2

  // The net line breaks wherever payroll is missing or a figure fails to
  // parse. Built from maximal runs of consecutive months, the same way the
  // breakeven chart breaks its cost line at a suppressed month.
  const netRuns: number[][] = []
  rows.forEach((row, index) => {
    if (netAt(row) === null) return
    const run = netRuns[netRuns.length - 1]
    if (run && run[run.length - 1] === index - 1) run.push(index)
    else netRuns.push([index])
  })

  const active = hover === null ? null : (rows[hover.index] ?? null)
  const hoveredGap = hover === null ? null : (gap[hover.index - rows.length] ?? null)

  return (
    <section className="panel chart-panel">
      <header className="chart-head">
        <div>
          <h2>Cash in and out</h2>
          <SourceNote text={sourceNote ?? null} />
          <p className="chart-sub">
            Booked AR and fundraise above the line, payroll and costs below it, net cash as the
            line. Hatched costs come from the headcount plan: payroll where none has been reported,
            plus planned hires and opex.{' '}
            {showPipeline ? (
              <>
                Hatched inflow is weighted pipeline: deals not yet signed, counted at the FCF
                conversion rate. The net line includes it.
              </>
            ) : (
              <>
                Pipeline (deals not yet signed) is left out, so this is the current-status view:
                money already booked against the costs we expect.
              </>
            )}
          </p>
        </div>
        <div className="chart-controls">
          <PipelineToggle
            on={showPipeline}
            onChange={onShowPipelineChange}
            affected={pipelineMonths}
            total={months.length}
          />
          <ul className="legend">
            <li>
              <span className="swatch" style={{ background: INFLOW_RAMP.ar }} /> Booked AR
            </li>
            <li>
              <span className="swatch" style={{ background: INFLOW_RAMP.fundraise }} /> Fundraise
            </li>
            {showPipeline && (
              <li>
                <span className="swatch" style={{ background: hatchSwatch(INFLOW_RAMP.ar) }} />{' '}
                Pipeline (weighted)
              </li>
            )}
            <li>
              <span className="swatch" style={{ background: POLARITY.negative }} /> {payrollLabel}
            </li>
            <li>
              <span className="swatch" style={{ background: hatchSwatch(POLARITY.negative) }} />{' '}
              Planned costs
            </li>
            <li>
              <span className="swatch line" style={{ background: SERIES.yellow }} />{' '}
              {showPipeline ? 'Net cash incl. pipeline' : 'Net cash'}
            </li>
          </ul>
        </div>
      </header>

      <div className="chart-wrap">
        <svg
          viewBox={`0 0 ${W} ${H}`}
          className="chart"
          role="img"
          aria-label={
            showPipeline
              ? 'Monthly cash in and out with net cash, including weighted pipeline. Planned costs and pipeline are drawn as hatched bars. The same figures are in the cash flow table below.'
              : 'Monthly cash in and out with net cash from booked money and expected costs; pipeline is left out. Planned costs are drawn as hatched bars. The same figures are in the cash flow table below.'
          }
          onMouseLeave={() => setHover(null)}
        >
          <defs>
            {/* Plan and forecast fills: the same colour as the reported quantity
                they belong to, hatched so a forecast reads as a forecast at a
                glance (invariant #3 — the legend says it out loud too). Texture
                rather than a third and fourth hue, which would fail the palette
                checks and would claim these are different quantities. */}
            <pattern
              id="plannedAp"
              width={6}
              height={6}
              patternUnits="userSpaceOnUse"
              patternTransform="rotate(45)"
            >
              <rect width={6} height={6} fill={POLARITY.negative} opacity={0.18} />
              <line x1={0} y1={0} x2={0} y2={6} stroke={POLARITY.negative} strokeWidth={2} />
            </pattern>
            <pattern
              id="forecastAr"
              width={6}
              height={6}
              patternUnits="userSpaceOnUse"
              patternTransform="rotate(45)"
            >
              <rect width={6} height={6} fill={INFLOW_RAMP.ar} opacity={0.18} />
              <line x1={0} y1={0} x2={0} y2={6} stroke={INFLOW_RAMP.ar} strokeWidth={2} />
            </pattern>
          </defs>

          {gap.length > 0 && (
            <NoDataZone
              x={PAD.left + band * rows.length}
              width={band * gap.length}
              top={PAD.top}
              height={PLOT_H}
              lastMonth={lastMonth}
            />
          )}

          {ticksFor(min, max).map((tick) => (
            <g key={tick}>
              <line
                x1={PAD.left}
                x2={W - PAD.right}
                y1={y(tick)}
                y2={y(tick)}
                stroke={Math.abs(tick) < 1e-9 ? CHART_INK.axis : CHART_INK.grid}
                strokeWidth={1}
              />
              <text x={PAD.left - 8} y={y(tick) + 3} textAnchor="end" className="tick">
                {compactYen(tick)}
              </text>
            </g>
          ))}

          {rows.map((row, index) => {
            const x = centre(index) - barW / 2
            // Costs are drawn in both states (plan-basis payroll hatched); only
            // the pipeline inflow follows the switch.
            const drawAp = row.ap !== null
            const drawForecastIn = showPipeline && row.forecastIn > 0
            const drawForecastOut = row.forecastOut > 0
            const arTop = y(row.ar)
            const fundTop = y(row.ar + row.fundraise)
            const inTop = y(row.ar + row.fundraise + row.forecastIn)
            // Forecast out hangs off the bottom of the payroll bar, or off the
            // zero line when there is no payroll bar to hang from.
            const outFrom = drawAp ? (row.ap ?? 0) : 0
            const outGap = drawAp ? 2 : 0
            return (
              <g key={row.month.month}>
                {/* Segments are separated by a 2px gap of surface rather than a
                    border — the boundary reads without drawing a line. */}
                {drawForecastIn && (
                  <rect
                    x={x}
                    y={inTop}
                    width={barW}
                    height={Math.max(0, fundTop - inTop - 2)}
                    rx={3}
                    fill="url(#forecastAr)"
                  />
                )}
                {row.fundraise !== 0 && (
                  <rect
                    x={x}
                    y={fundTop}
                    width={barW}
                    height={Math.max(0, arTop - fundTop - 2)}
                    rx={3}
                    fill={INFLOW_RAMP.fundraise}
                  />
                )}
                <rect
                  x={x}
                  y={arTop}
                  width={barW}
                  height={Math.max(0, zero - arTop)}
                  rx={3}
                  fill={INFLOW_RAMP.ar}
                />
                {drawAp && (
                  <rect
                    x={x}
                    y={zero}
                    width={barW}
                    height={Math.max(0, y(row.ap ?? 0) - zero)}
                    rx={3}
                    fill={row.planned ? 'url(#plannedAp)' : POLARITY.negative}
                  />
                )}
                {drawForecastOut && (
                  <rect
                    x={x}
                    y={y(outFrom) + outGap}
                    width={barW}
                    height={Math.max(0, y(outFrom - row.forecastOut) - y(outFrom) - outGap)}
                    rx={3}
                    fill="url(#plannedAp)"
                  />
                )}
              </g>
            )
          })}

          {netRuns.map((run) => (
            <polyline
              key={`net-${run[0]}`}
              points={run
                .map((index) => `${centre(index)},${y(netAt(rows[index] as Row) ?? 0)}`)
                .join(' ')}
              fill="none"
              stroke={SERIES.yellow}
              strokeWidth={2}
              strokeLinejoin="round"
              strokeLinecap="round"
            />
          ))}

          {rows.map((row, index) => {
            const net = netAt(row)
            return net === null ? null : (
              <circle
                key={`net-${row.month.month}`}
                cx={centre(index)}
                cy={y(net)}
                r={hover?.index === index ? 5 : 3.5}
                fill={SERIES.yellow}
                stroke="#272C47"
                strokeWidth={2}
              />
            )
          })}

          {[...rows.map((row) => row.month.month), ...gap].map((month, index) => (
            <text key={`x-${month}`} x={centre(index)} y={LABEL_Y} className="tick" textAnchor="end" transform={`rotate(-35 ${centre(index)} ${LABEL_Y})`}>
              {monthLabel(month).replace(' ', ' ')}
            </text>
          ))}

          {/* Hit targets deliberately wider than the bars. */}
          {rows.map((row, index) => (
            <rect
              key={`hit-${row.month.month}`}
              x={PAD.left + band * index}
              y={PAD.top}
              width={band}
              height={PLOT_H}
              fill="transparent"
              onMouseEnter={() => setHover({ index, x: centre(index) })}
            />
          ))}
          {gap.map((month, offset) => (
            <rect
              key={`hit-${month}`}
              x={PAD.left + band * (rows.length + offset)}
              y={PAD.top}
              width={band}
              height={PLOT_H}
              fill="transparent"
              onMouseEnter={() =>
                setHover({ index: rows.length + offset, x: centre(rows.length + offset) })
              }
            />
          ))}

          {hover !== null && (
            <line
              x1={hover.x}
              x2={hover.x}
              y1={PAD.top}
              y2={PAD.top + PLOT_H}
              stroke={CHART_INK.axis}
              strokeWidth={1}
              pointerEvents="none"
            />
          )}
        </svg>

        {hover !== null && hoveredGap !== null && (
          <div className="tooltip" style={{ left: `${(hover.x / W) * 100}%` }}>
            <strong>{monthLabel(hoveredGap)}</strong>
            <span className="tooltip-note">No data for this month</span>
          </div>
        )}

        {hover !== null && active !== null && (
          <div className="tooltip" style={{ left: `${(hover.x / W) * 100}%` }}>
            <strong>{monthLabel(active.month.month)}</strong>
            <span>
              Booked AR <b>{yen(active.month.actualAr)}</b>
            </span>
            {active.fundraise !== 0 && (
              <span>
                Fundraise <b>{yen(active.month.actualFundraise)}</b>
              </span>
            )}
            {showPipeline && active.forecastIn > 0 && (
              <span>
                Pipeline (weighted) <b>{yen(magnitude(active.month.forecastNetNewAr))}</b>
              </span>
            )}
            <span>
              Payroll{' '}
              <b>
                {active.month.apBasis === 'actual'
                  ? yen(active.month.actualAp)
                  : active.month.apBasis === 'plan'
                    ? `${yen(active.month.planPayroll)} (planned)`
                    : 'not reported'}
              </b>
            </span>
            {active.forecastOut > 0 && (
              <span>
                Planned costs <b>−{yen(magnitude(active.month.forecastNetNewAp))}</b>
              </span>
            )}
            {active.payrollKnown ? (
              <span>
                {showPipeline ? 'Net incl. pipeline' : 'Net cash'}{' '}
                <b>{showPipeline ? yen(active.month.netCashDifference) : yen(active.bookedNet)}</b>
              </span>
            ) : (
              <span className="tooltip-note">no net: payroll neither reported nor planned</span>
            )}
            {!showPipeline && hasPipeline(active.month) && (
              <span className="tooltip-note">
                excludes {yen(active.month.forecastNetNewAr)} weighted pipeline
              </span>
            )}
          </div>
        )}
      </div>
    </section>
  )
}
